import dayjs from "dayjs";
import { ChangingDirectionType, ChangingParameters, Restrictions } from "../types";
import { assetService } from "../../../shared/services/AssetService";
import { OptionInfoDto } from "../../../features/options-table/types/OptionInfoDto";
import { portfolioMappers } from "../../../shared/mappers/PortfolioMappers";
import { AssetType } from "../../../shared/types/AssetType";
import { BlackScholesModelCalculationService } from "../../../shared/services/BlackScholesModelCalculationService";

class OptionParametersSelectorManager {
    isCommandApplicable = (parameters: ChangingParameters) =>
        parameters.selectedOrder !== null &&
        !parameters.selectedOrder.locked &&
        assetService.isOption(parameters.selectedOrder!) &&
        parameters.selectedOrder.expirationPrice == null;
    
    changeExpiration = async (
        changingDirectionType: ChangingDirectionType, 
        parameters: ChangingParameters
    ): Promise<void> => {
        const selectedOrder = parameters.selectedOrder;
        
        if (
            parameters.marketDataDto === undefined ||
            selectedOrder === null
        ) {
            return;
        }

        const order = parameters.portfolioItems.find(x => x.externalId === selectedOrder.externalId);

        if (order === undefined || !assetService.isOption(selectedOrder))
            return;

        const currentExpiration = order.expireAt!;
        const sortedExpirations = [...parameters.marketDataDto.Expirations]
            .sort((x, y) =>
                changingDirectionType == ChangingDirectionType.Next
                    ? x < y ? 1 : -1
                    : x > y ? 1 : -1);

        if (!this.canChangeExpiration(sortedExpirations, currentExpiration))
            return;

        const targetExpiration = this.getTargetExpiration(sortedExpirations, currentExpiration);

        if (dayjs(targetExpiration).isBefore(dayjs()))
            return;
        
        // Get all options with target expiration sorted by Strike ascending
        const optionInfoDtos = parameters.marketDataDto.OptionInfos
            .filter(x =>
                x.Type === order.type &&
                dayjs(x.ExpireAt!).startOf('day').isSame(dayjs(targetExpiration).startOf('day')))
            .sort((x, y) => x.Strike > y.Strike ? 1 : -1)

        // Seek of the exact strike or the closest one for the target expiration
        const getOptionWithClosestStrike = (): OptionInfoDto | null => {
            if (optionInfoDtos.length === 1) {
                return optionInfoDtos[0];
            } else {
                if (optionInfoDtos[0].Strike >= order.strike!){
                    return optionInfoDtos[0];
                }
                else if (optionInfoDtos[optionInfoDtos.length-1].Strike <= order.strike!)
                    return optionInfoDtos[optionInfoDtos.length-1];

                for(let x = 1; x < optionInfoDtos.length; x++) {
                    if (optionInfoDtos[x].Strike === order.strike!)
                        return optionInfoDtos[x];

                    if (optionInfoDtos[x].Strike > order.strike!) {
                        return order.strike! - optionInfoDtos[x - 1].Strike > optionInfoDtos[x].Strike - order.strike!
                            ? optionInfoDtos[x - 1]
                            : optionInfoDtos[x];
                    }
                }
            }

            return null;
        }
        
        const optionInfoDto = getOptionWithClosestStrike();
        
        if (optionInfoDto === null || optionInfoDto === undefined)
            return;

        order.expireAt = optionInfoDto.ExpireAt;
        order.markImpliedVolatility = optionInfoDto.MarkImpliedVolatility!;
        order.symbol = optionInfoDto.Symbol;

        const riskFreeInterestValue = BlackScholesModelCalculationService.riskFreeRate;
        
        const { price, delta, gamma, vega, theta } =
            portfolioMappers.getAssetParameters(order.type, order.strike, order.expireAt,
                order.markImpliedVolatility, riskFreeInterestValue, parameters.marketDataDto!.UnderlyingPrice);

        order.openPrice = price;
        order.markPrice = price;
        order.delta = delta * order.size;
        order.gamma = (gamma ?? 0) * order.size;
        order.vega = (vega ?? 0) * order.size;
        order.theta = (theta ?? 0) * order.size;

        order.setChangedState();
        
        await parameters.updateItemInSelectedPortfolio(false, false);
    }
    
    changeStrike = async (
        changingDirectionType: ChangingDirectionType,
        parameters: ChangingParameters
    ): Promise<void> => {
        const selectedOrder = parameters.selectedOrder;

        if (
            parameters.marketDataDto === undefined ||
            selectedOrder === null
        ) {
            return;
        }

        const order = parameters.portfolioItems.find(x => x.externalId === selectedOrder.externalId);

        if (order === undefined || !assetService.isOption(selectedOrder))
            return;

        const currentStrike = order.strike!;
        
        const sortedStrikes = this.getStrikes(
            parameters.marketDataDto.OptionInfos,
            order.type, order.expireAt!,
            (x, y) =>
                changingDirectionType == ChangingDirectionType.Next
                    ? x < y ? 1 : -1
                    : x > y ? 1 : -1);
        
        if (!this.canChangeStrike(sortedStrikes, currentStrike))
            return;

        const targetStrike = this.getTargetStrike(sortedStrikes, currentStrike);

        if (targetStrike === null)
            return;
        
        const optionInfoDto = this.getOption(
            parameters.marketDataDto.OptionInfos,
            order.type, order.expireAt!, targetStrike);
        
        if (optionInfoDto === null)
            return;
        
        order.strike = optionInfoDto.Strike;
        order.markImpliedVolatility = optionInfoDto.MarkImpliedVolatility;
        order.symbol = optionInfoDto.Symbol;

        const riskFreeInterestValue = BlackScholesModelCalculationService.riskFreeRate;
        
        const { price, delta, gamma, vega, theta } =
            portfolioMappers.getAssetParameters(order.type, order.strike, order.expireAt,
                order.markImpliedVolatility, riskFreeInterestValue,
                parameters.marketDataDto!.UnderlyingPrice);

        order.openPrice = price;
        order.markPrice = price;
        order.delta = delta * order.size;
        order.gamma = (gamma ?? 0) * order.size;
        order.vega = (vega ?? 0) * order.size;
        order.theta = (theta ?? 0) * order.size;

        order.setChangedState();
        
        await parameters.updateItemInSelectedPortfolio(false, false);
    }

    getStrikes = (
        optionInfos: OptionInfoDto[],
        optionsType: AssetType,
        expiration: Date,
        sort: (x: number, y: number) => number
    ): number[] => {
        return optionInfos
            .filter(x =>
                x.Type === optionsType &&
                dayjs(x.ExpireAt!).startOf('day').isSame(dayjs(expiration).startOf('day')))
            .map(x => x.Strike)
            .sort(sort);
    }

    getClosestStrike = (strikes: number[], strike: number): number | null => {
        if (strikes === null || strikes === undefined || strikes.length === 0)
            return null;
        
        if (strikes.length === 1) {
            return strikes[0];
        } else {
            if (strikes[0] >= strike){
                return strikes[0];
            }
            else if (strikes[strikes.length-1] <= strike)
                return strikes[strikes.length-1];

            for(let x = 1; x < strikes.length; x++) {
                if (strikes[x] === strike)
                    return strikes[x];

                if (strikes[x] > strike) {
                    return strike - strikes[x - 1] > strikes[x] - strike
                        ? strikes[x - 1]
                        : strikes[x];
                }
            }
            
            return strikes[strikes.length - 1];
        }
    }
    
    getOption = (
        optionInfos: OptionInfoDto[],
        optionsType: AssetType,
        expiration: Date,
        strike: number
    ): OptionInfoDto | null => {
        const optionInfoDtos = optionInfos
            .filter(x =>
                x.Type === optionsType &&
                dayjs(x.ExpireAt!).startOf('day').isSame(dayjs(expiration).startOf('day')) &&
                x.Strike === strike);

        if (optionInfoDtos === null || optionInfoDtos.length === 0)
            return null;

        return optionInfoDtos[0];
    }
    
    private getTargetExpiration = (sortedExpirations: Date[], currentExpiration:Date): Date | null => {
        for (let x = 0; x < sortedExpirations.length; x++) {
            if (dayjs(sortedExpirations[x]).startOf('day').isSame(dayjs(currentExpiration).startOf('day')))
                return sortedExpirations[x-1];
        }

        return null;
    }

    private getTargetStrike = (sortedStrikes: number[], currentStrike:number): number | null => {
        for (let x = 0; x < sortedStrikes.length; x++) {
            if (sortedStrikes[x] === currentStrike)
                return sortedStrikes[x-1];
        }

        return null;
    }

    private canChangeExpiration = (
        sortedExpirations: Date[],
        currentExpiration: Date
    ): boolean => {
        return sortedExpirations.length > 0 &&
            !dayjs(sortedExpirations[0]).startOf('day').isSame(dayjs(currentExpiration).startOf('day'));
    }

    private canChangeStrike = (
        sortedStrikes: number[],
        currentStrike: number
    ): boolean => {
        return sortedStrikes.length > 0 && sortedStrikes[0] !== currentStrike;
    }

    getExpirationRestrictions = (parameters: ChangingParameters): Restrictions | null => {
        const selectedOrder = parameters.selectedOrder;
        const marketDataDto = parameters.marketDataDto;

        if (marketDataDto === undefined || selectedOrder === null) {
            return {
                canChangeToNext: false,
                canChangeToPrevious: false
            }
        }

        const order = parameters.portfolioItems.find(x => x.externalId === selectedOrder.externalId)!;

        if (order === null || order === undefined)
            return null;
        
        const sortedExpirations = [...marketDataDto.Expirations]
            .sort((x, y) => x < y ? 1 : -1);

        return {
            canChangeToNext: this.canChangeExpiration(sortedExpirations, order.expireAt!),
            canChangeToPrevious: this.canChangeExpiration(sortedExpirations.sort((x, y) => x > y ? 1 : -1), order.expireAt!),
        }
    }

    getStrikeRestrictions = (parameters: ChangingParameters): Restrictions | null => {
        const selectedOrder = parameters.selectedOrder;
        const marketDataDto = parameters.marketDataDto;
        
        if (marketDataDto === undefined || selectedOrder === null) {
            return {
                canChangeToNext: false,
                canChangeToPrevious: false
            }
        }

        const order = parameters.portfolioItems.find(x => x.externalId === selectedOrder.externalId)!;

        if (order === null || order === undefined)
            return null;
        
        const sortedStrikes = marketDataDto.OptionInfos
            .filter(x =>
                x.Type === order.type &&
                dayjs(x.ExpireAt!).startOf('day').isSame(dayjs(order.expireAt).startOf('day')))
            .map(x => x.Strike)
            .sort((x, y) => x < y ? 1 : -1);
        
        return {
            canChangeToNext: this.canChangeStrike(sortedStrikes, order.strike!),
            canChangeToPrevious: this.canChangeStrike(sortedStrikes.sort((x, y) => x > y ? 1 : -1), order.strike!),
        }
    }    
}

export const optionParametersSelectorManager = new OptionParametersSelectorManager();